import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { Landmark, Carrot, Briefcase, Wine, ExternalLink, Grape, GlassWater } from "lucide-react";

// Winemakers featured on our wine list, grouped by region.
// Many are distributed through our partner Caves Carrière.
const WINEMAKERS = {
  rhone: [
    "Domaine Talès — Entrechaux",
    "Mas Oncle Ernest — Entrechaux",
    "Le Mas des Flauzières — Entrechaux",
    "Domaine Champ-Long — Entrechaux",
    "Domaine d'Ouréa — Vacqueyras",
    "Coteaux des Travers — Rasteau",
    "Domaine Grand Veneur — Alain Jaume",
    "Vignoble Alain Ignace",
    "Ogier Michel & Stéphane",
  ],
  luberon: [
    "Domaine de Maslauris",
  ],
  bourgogne: [
    "Maison Jean Chartron — Puligny-Montrachet",
    "Domaine Amiot-Servelle — Chambolle-Musigny",
    "Rodolphe Demougeot — Meursault",
    "Baillot Arnaud",
    "Jouan Olivier",
    "Pierre & J-B Lebreuil",
    "Domaine Hubert Lignier",
    "Domaine Arthur Gras — Saint-Romain",
    "Domaine Alain Gras — Saint-Romain",
    "Chanson Père & Fils",
    "Domaine Sébastien Dampt",
    "Domaine Julie Fèvre",
    "Domaine Larue — Saint-Aubin",
    "Domaine Thévenard",
  ],
  champagne: [
    "Champagne Deutz",
    "Champagne Philipponnat",
    "Champagne Bollinger",
  ],
  loire: ["Domaine François Crochet — Sancerre"],
  provence: ["Domaine Saint André de Figuière — Famille Combard"],
  alsace: ["Domaine Jean-Marie Haag", "Maison Trimbach"],
  bordeaux: ["Château La Tour Blanche"],
  corse: ["Domaine Novella"],
};

const WINEMAKER_REGIONS = [
  { id: "rhone", color: "#C1624D" },
  { id: "luberon", color: "#9A7BC1" },
  { id: "bourgogne", color: "#8B6F4E" },
  { id: "champagne", color: "#A5C4D4" },
  { id: "loire", color: "#6F7D4E" },
  { id: "provence", color: "#D89B7A" },
  { id: "alsace", color: "#7A8AB0" },
  { id: "bordeaux", color: "#8B3A3A" },
  { id: "corse", color: "#5D8A6A" },
];

// Spirits / distilleries we work with — partners only, grouped by spirit family.
// Brands only (no individual bottles, no volumes/abv — see "Cave" for the full list).
const SPIRITS = {
  cognac: ["Camus", "Crocodile Bros", "Delamain"],
  armagnac: ["Château de Lacquy"],
  whisky: ["Jack Daniel's", "Michel Couvreur", "MBK"],
  rhum: [
    "Havana Club",
    "Savanna",
    "Bielle",
    "La Compagnie des Indes",
    "Aguacana",
  ],
  vodka: ["Romanov"],
  gin: ["Distillerie Maralpa", "Pegasus Distillerie"],
  liqueurs: [
    "Chartreuse",
    "Get 27",
    "Limoncello Sorrentini",
    "Baillot Arnaud",
    "Distillerie Maralpa",
  ],
  porto: ["Taylor's"],
};

const SPIRIT_CATEGORIES = [
  { id: "cognac", color: "#8B6F4E" },
  { id: "armagnac", color: "#A6503C" },
  { id: "whisky", color: "#5D544F" },
  { id: "rhum", color: "#C1624D" },
  { id: "vodka", color: "#7A8AB0" },
  { id: "gin", color: "#6F7D4E" },
  { id: "liqueurs", color: "#A5C4D4" },
  { id: "porto", color: "#8B3A3A" },
];

// Partner data — order within each category controls display order.
// `domain` is used to fetch the official favicon as a logo.
// `logoUrl` (optional) takes precedence over the auto-fetched favicon when set.
// If everything fails, a stylized initials tile is shown.
const PARTNERS = [
  // Institutionnels & syndicat
  { id: "umih", name: "UMIH 84", domain: "umih.fr", url: "https://www.umih.fr", category: "institutional" },
  { id: "sacem", name: "SACEM", domain: "sacem.fr", url: "https://clients.sacem.fr/", category: "institutional" },
  { id: "mairie", name: "Mairie d'Entrechaux", domain: "entrechaux.fr", url: "https://www.entrechaux.fr", category: "institutional" },
  // Produits frais & épicerie
  { id: "salade2fruits", name: "Salade2Fruits", domain: "salade2fruits.eu", url: "https://www.salade2fruits.eu/contact", category: "fresh" },
  { id: "episaveurs", name: "EpiSaveurs", domain: "episaveurs.fr", url: "https://www.episaveurs.fr", category: "fresh" },
  { id: "metro", name: "Metro", domain: "metro.fr", url: "https://www.metro.fr", category: "fresh" },
  // Services pro
  { id: "boom", name: "BOOM", domain: "boom-studio.fr", url: null, category: "services" },
  { id: "elis", name: "Elis", domain: "fr.elis.com", url: "https://fr.elis.com", category: "services" },
  { id: "jlpsystemes", name: "JLP Systèmes", domain: "jlpsystemes.com", url: "https://www.jlpsystemes.com", category: "services" },
  { id: "banque_populaire", name: "Banque Populaire Méditerranée — Vaison-la-Romaine", domain: "banquepopulaire.fr", url: "https://agences.banquepopulaire.fr/banque-assurance/vaison-la-romaine", category: "services" },
  // Boissons
  { id: "caves_carriere", name: "Caves Carrière", domain: "carriere-distribution.fr", url: "https://carriere-distribution.fr/fr/", category: "drinks" },
  { id: "bollinger_diffusion", name: "Bollinger Diffusion", domain: "bollinger-selection.fr", url: "https://www.bollinger-selection.fr", category: "drinks" },
  { id: "philipponnat", name: "Champagne Philipponnat", domain: "philipponnat.com", url: "https://www.philipponnat.com", category: "drinks" },
  { id: "maralpa", name: "Distillerie Maralpa", domain: "distillerie-maralpa.fr", url: "https://distillerie-maralpa.fr", category: "drinks" },
  { id: "paradisiac", name: "Paradisiac", domain: "crocodilebros.com", url: "https://crocodilebros.com", category: "drinks" },
  { id: "france_boissons", name: "France Boissons", domain: "france-boissons.fr", url: "https://www.france-boissons.fr", category: "drinks" },
];

const CATEGORIES = [
  { id: "institutional", icon: Landmark, color: "#A5C4D4" },
  { id: "fresh", icon: Carrot, color: "#6F7D4E" },
  { id: "services", icon: Briefcase, color: "#8B6F4E" },
  { id: "drinks", icon: Wine, color: "#C1624D" },
];

const getInitials = (name) =>
  name
    .replace(/[^A-Za-zÀ-ÿ0-9 ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const PartnerLogo = ({ partner, color }) => {
  const initials = getInitials(partner.name);
  // Cascade of logo sources: hosted file (if provided) -> Google favicon -> DDG icon.
  // The <img> falls through onError until one source loads (or all fail -> initials).
  const sources = [
    partner.logoUrl,
    `/partners/${partner.id}.png`,
    `https://www.google.com/s2/favicons?domain=${partner.domain}&sz=128`,
    `https://icons.duckduckgo.com/ip3/${partner.domain}.ico`,
  ].filter(Boolean);
  const [sourceIndex, setSourceIndex] = useState(0);
  const allFailed = sourceIndex >= sources.length;

  if (allFailed) {
    return (
      <div
        className="w-16 h-16 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}1A`, color }}
        data-testid={`partner-logo-fallback-${partner.id}`}
      >
        <span
          className="text-lg font-medium tracking-wide"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {initials}
        </span>
      </div>
    );
  }

  return (
    <div
      className="w-16 h-16 rounded-md bg-white border border-[#D8D0C5]/60 flex items-center justify-center flex-shrink-0 overflow-hidden"
      data-testid={`partner-logo-${partner.id}`}
    >
      <img
        src={sources[sourceIndex]}
        alt={`${partner.name} logo`}
        className="max-w-[78%] max-h-[78%] object-contain"
        loading="lazy"
        onError={() => setSourceIndex((i) => i + 1)}
      />
    </div>
  );
};

const PartnerCard = ({ partner, color, t, index }) => {
  const isLink = Boolean(partner.url);
  const Wrapper = isLink ? motion.a : motion.div;
  const wrapperProps = isLink
    ? { href: partner.url, target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className={`group bg-white/80 backdrop-blur-sm p-5 border border-[#D8D0C5] rounded-sm
                 transition-all duration-300 flex items-start gap-4
                 ${isLink ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : ""}`}
      data-testid={`partner-card-${partner.id}`}
    >
      <PartnerLogo partner={partner} color={color} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4
            className={`text-lg font-normal text-[#2C2420] transition-colors duration-300 truncate ${isLink ? "group-hover:text-[#C1624D]" : ""}`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {partner.name}
          </h4>
          {isLink && (
            <ExternalLink
              size={14}
              className="text-[#8C847F] group-hover:text-[#C1624D] transition-colors duration-300 flex-shrink-0 mt-1.5"
              aria-label={t("partners.visit_website")}
            />
          )}
        </div>
        <p
          className="text-sm text-[#5D544F] leading-snug"
          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
        >
          {t(`partners.p_${partner.id}_desc`)}
        </p>
      </div>
    </Wrapper>
  );
};

const CategoryBlock = ({ category, partners, t }) => {
  const Icon = category.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="mb-14 last:mb-0"
      data-testid={`partner-category-${category.id}`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${category.color}1A` }}
        >
          <Icon size={18} style={{ color: category.color }} />
        </div>
        <h3
          className="text-xl md:text-2xl font-light text-[#2C2420]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {t(`partners.cat_${category.id}`)}
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
        {partners.map((p, i) => (
          <PartnerCard key={p.id} partner={p} color={category.color} t={t} index={i} />
        ))}
      </div>
    </motion.div>
  );
};

export const PartnersSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="partenaires"
      data-testid="partners-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-[#F5E6D3]/40"
    >
      <div className="max-w-6xl mx-auto" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#C1624D] mb-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("partners.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-light tracking-tight text-[#2C2420] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="partners-heading"
          >
            {t("partners.heading")}
          </h2>
          <div className="ornament-divider max-w-xs mx-auto">
            <span
              className="text-[#C1624D] text-2xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              &bull;
            </span>
          </div>
          <p
            className="text-base text-[#8C847F] mt-4 max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("partners.subtitle")}
          </p>
        </motion.div>

        {/* Categories with partner cards */}
        <div>
          {CATEGORIES.map((cat) => {
            const partners = PARTNERS.filter((p) => p.category === cat.id);
            if (partners.length === 0) return null;
            return (
              <CategoryBlock
                key={cat.id}
                category={cat}
                partners={partners}
                t={t}
              />
            );
          })}
        </div>

        {/* Winemakers — featured on our wine list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="mt-8 mb-6"
          data-testid="partners-winemakers"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#C1624D1A" }}
            >
              <Grape size={18} className="text-[#C1624D]" />
            </div>
            <h3
              className="text-xl md:text-2xl font-light text-[#2C2420]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t("partners.winemakers_title")}
            </h3>
          </div>
          <p
            className="text-sm md:text-base text-[#5D544F] leading-relaxed mb-8 max-w-3xl"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("partners.winemakers_intro")}{" "}
            <a
              href="https://carriere-distribution.fr/fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C1624D] underline underline-offset-2 hover:opacity-70 transition-opacity"
            >
              Caves Carrière
            </a>
            .
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {WINEMAKER_REGIONS.map((region, ri) => {
            const list = WINEMAKERS[region.id] || [];
            if (list.length === 0) return null;
            return (
              <motion.div
                key={region.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: ri * 0.05 }}
                className="bg-white/70 backdrop-blur-sm p-5 border border-[#D8D0C5] rounded-sm"
                data-testid={`winemakers-region-${region.id}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="w-1.5 h-6 rounded-full"
                    style={{ backgroundColor: region.color }}
                  />
                  <h4
                    className="text-lg font-normal text-[#2C2420]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {t(`partners.region_${region.id}`)}
                  </h4>
                  <span
                    className="ml-auto text-xs text-[#8C847F]"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  >
                    {list.length}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {list.map((name) => (
                    <li
                      key={name}
                      className="text-sm text-[#5D544F] leading-snug flex items-start gap-2"
                      style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                    >
                      <span
                        className="inline-block w-1 h-1 rounded-full flex-shrink-0 mt-2"
                        style={{ backgroundColor: region.color }}
                      />
                      {name}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Spirits — distillers / houses on our menu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="mt-14 mb-6"
          data-testid="partners-spirits"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#8B6F4E1A" }}
            >
              <GlassWater size={18} className="text-[#8B6F4E]" />
            </div>
            <h3
              className="text-xl md:text-2xl font-light text-[#2C2420]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t("partners.spirits_title")}
            </h3>
          </div>
          <p
            className="text-sm md:text-base text-[#5D544F] leading-relaxed mb-8 max-w-3xl"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("partners.spirits_intro")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {SPIRIT_CATEGORIES.map((cat, ci) => {
            const list = SPIRITS[cat.id] || [];
            if (list.length === 0) return null;
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: ci * 0.05 }}
                className="bg-white/70 backdrop-blur-sm p-5 border border-[#D8D0C5] rounded-sm"
                data-testid={`spirits-cat-${cat.id}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="w-1.5 h-6 rounded-full"
                    style={{ backgroundColor: cat.color }}
                  />
                  <h4
                    className="text-lg font-normal text-[#2C2420]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {t(`partners.spirit_${cat.id}`)}
                  </h4>
                  <span
                    className="ml-auto text-xs text-[#8C847F]"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  >
                    {list.length}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {list.map((brand) => (
                    <li
                      key={brand}
                      className="text-sm text-[#5D544F] leading-snug flex items-start gap-2"
                      style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                    >
                      <span
                        className="inline-block w-1 h-1 rounded-full flex-shrink-0 mt-2"
                        style={{ backgroundColor: cat.color }}
                      />
                      <span
                        className="flex-1 text-[#2C2420]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "0.98rem" }}
                      >
                        {brand}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-14"
        >
          <p
            className="text-base text-[#5D544F] mb-2"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("partners.cta_lead")}
          </p>
          <a
            href="#nous-trouver"
            onClick={(e) => {
              e.preventDefault();
              document
                .querySelector("#nous-trouver")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 text-[#C1624D] text-sm uppercase tracking-[0.15em]
                       hover:opacity-70 transition-opacity duration-300 underline underline-offset-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
            data-testid="partners-contact-link"
          >
            {t("partners.cta_link")}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
